import type {
	CoursesAdminSetUserRoleDto,
	CoursesAdminUserNeighborsQueryDto,
	CoursesAdminUsersFilterDto,
	CoursesListUsersQueryDto,
	CoursesStudentsOverviewQueryDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	ExportUsersInput,
	ExportUsersOutput,
	GetAdminUserNeighborsInput,
	GetAdminUserNeighborsOutput,
	ListUsersWithRolesInput,
	ListUsersWithRolesOutput,
	SetUserRoleInput,
	SetUserRoleOutput,
	AdminListLastLoginsOutput,
	GetStudentProgressInput,
	GetStudentProgressOutput,
	ListStudentsOverviewInput,
	ListStudentsOverviewOutput,
} from '../../types/admin.types';

const TAG = 'courses:admin' as const;
const INVALIDATES = [TAG] as const;

export const adminEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		listUsersWithRoles: builder.query<ListUsersWithRolesOutput, ListUsersWithRolesInput | void>({
			query: (params) => ({
				url: 'courses/admin/users',
				params: (params ?? {}) satisfies Wire<CoursesListUsersQueryDto>,
			}),
			providesTags: [TAG],
		}),
		getAdminUserNeighbors: builder.query<GetAdminUserNeighborsOutput, GetAdminUserNeighborsInput>({
			query: ({ userId, ...params }) => ({
				url: `courses/admin/users/${encodeURIComponent(userId)}/neighbours`,
				params: params satisfies Wire<CoursesAdminUserNeighborsQueryDto>,
			}),
			providesTags: [TAG],
		}),
		exportUsers: builder.mutation<ExportUsersOutput, ExportUsersInput>({
			query: (body) => ({
				url: 'courses/admin/users/export',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminUsersFilterDto>,
			}),
		}),
		setUserRole: builder.mutation<SetUserRoleOutput, SetUserRoleInput>({
			query: ({ userId, ...body }) => ({
				url: `courses/admin/users/${encodeURIComponent(userId)}/role`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesAdminSetUserRoleDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListLastLogins: builder.query<AdminListLastLoginsOutput, void>({
			query: () => ({ url: 'courses/admin/users/last-logins' }),
			providesTags: [TAG],
		}),
		getStudentProgress: builder.query<GetStudentProgressOutput, GetStudentProgressInput>({
			query: ({ userId }) => ({
				url: `courses/admin/users/${encodeURIComponent(userId)}/progress`,
			}),
			providesTags: [TAG],
		}),
		listStudentsOverview: builder.query<
			ListStudentsOverviewOutput,
			ListStudentsOverviewInput | void
		>({
			query: (params) => ({
				url: 'courses/admin/users/students-overview',
				params: (params ?? {}) satisfies Wire<CoursesStudentsOverviewQueryDto>,
			}),
			providesTags: [TAG],
		}),
	}),
});

export const {
	useListUsersWithRolesQuery,
	useLazyListUsersWithRolesQuery,
	useGetAdminUserNeighborsQuery,
	useExportUsersMutation,
	useSetUserRoleMutation,
	useAdminListLastLoginsQuery,
	useLazyAdminListLastLoginsQuery,
	useGetStudentProgressQuery,
	useLazyGetStudentProgressQuery,
	useListStudentsOverviewQuery,
	useLazyListStudentsOverviewQuery,
} = adminEndpoints;
